/**
 * A tab that still runs the bundle of the previous deploy asks for chunks the
 * server no longer has: the next lazy page (or any other dynamic import)
 * fails with a browser-specific message. A full reload loads the new bundle
 * and lands on the URL the router already switched to.
 *
 * The reload happens at most once per {@link STALE_BUNDLE_RELOAD_GUARD_MS}:
 * when the chunk is still missing after it, the deploy itself is broken and
 * the error goes to the screen (and to Sentry) instead of looping.
 */

/** No second automatic reload within this time after the last one. */
export const STALE_BUNDLE_RELOAD_GUARD_MS = 30_000;

/** The `sessionStorage` key that holds the time of the last reload. */
export const STALE_BUNDLE_RELOAD_STORAGE_KEY = "atoms:staleBundleReloadAt";

/**
 * Lowercased parts of the messages browsers and Vite give a dynamic import or
 * a CSS preload whose file is gone (or answered with the HTML fallback).
 */
const STALE_BUNDLE_MESSAGES: readonly string[] = [
  // Chrome, Edge
  "failed to fetch dynamically imported module",
  // Firefox
  "error loading dynamically imported module",
  // Safari
  "importing a module script failed",
  // Vite's preload helper for the CSS of a chunk
  "unable to preload css",
];

const MAX_CAUSE_DEPTH = 5;

/**
 * A reload that has not unloaded the page after this time was cancelled
 * (a `beforeunload` prompt): the page is live again and may report.
 */
const RELOAD_PENDING_TIMEOUT_MS = 10_000;

let reloadPending = false;

/**
 * Whether this page has started a reload for a stale bundle. Error screens
 * shown in the meantime are not reported: the reload is the fix.
 */
export function isStaleBundleReloadPending(): boolean {
  return reloadPending;
}

/** Test helper: forget a started reload. */
export function resetStaleBundleReloadForTests(): void {
  reloadPending = false;
}

/**
 * Whether the browser knows it is offline: a dynamic import then fails with
 * the same message, but a reload would only show the browser's offline page.
 */
export function isBrowserOffline(): boolean {
  return globalThis.navigator?.onLine === false;
}

/** Whether an error comes from a chunk of a previous deploy that is gone. */
export function isStaleBundleError(error: unknown): boolean {
  let current: unknown = error;
  for (let depth = 0; depth < MAX_CAUSE_DEPTH && current; depth++) {
    if (!(current instanceof Error)) return false;
    const message = current.message.toLowerCase();
    if (STALE_BUNDLE_MESSAGES.some((part) => message.includes(part))) {
      return true;
    }
    current = current.cause;
  }
  return false;
}

export type StaleBundleReloadEnvironment = Readonly<{
  storage: Pick<Storage, "getItem" | "setItem"> | undefined;
  reload: () => void;
  now: () => number;
}>;

function defaultEnvironment(): StaleBundleReloadEnvironment {
  let storage: Storage | undefined;
  try {
    storage = globalThis.sessionStorage;
  } catch {
    // Blocked storage (privacy settings): no guard, so no automatic reload.
    storage = undefined;
  }
  return {
    storage,
    reload: () => globalThis.location.reload(),
    now: () => Date.now(),
  };
}

/**
 * Reloads the page to load the current bundle, unless it did so within
 * {@link STALE_BUNDLE_RELOAD_GUARD_MS}. Returns whether it reloads. Without
 * `sessionStorage` it cannot guard against a loop and does not reload.
 */
export function reloadForStaleBundle(
  environment: StaleBundleReloadEnvironment = defaultEnvironment(),
): boolean {
  const { storage, reload, now } = environment;
  if (reloadPending) return true;
  if (!storage) return false;
  const current = now();
  try {
    const stored = storage.getItem(STALE_BUNDLE_RELOAD_STORAGE_KEY);
    const last = stored === null ? Number.NaN : Number(stored);
    if (
      Number.isFinite(last) &&
      current - last < STALE_BUNDLE_RELOAD_GUARD_MS
    ) {
      return false;
    }
    storage.setItem(STALE_BUNDLE_RELOAD_STORAGE_KEY, String(current));
  } catch {
    return false;
  }
  reloadPending = true;
  // An error screen shown meanwhile is not reported afterwards either: the
  // user cancelled the reload, which is no defect, and can still reload.
  setTimeout(() => {
    reloadPending = false;
  }, RELOAD_PENDING_TIMEOUT_MS);
  reload();
  return true;
}

/**
 * For an error screen: when the error is a {@link isStaleBundleError} and the
 * page reloads for it, returns `true`; the screen then reports nothing.
 */
export function reloadIfStaleBundleError(error: unknown): boolean {
  return (
    isStaleBundleError(error) && !isBrowserOffline() && reloadForStaleBundle()
  );
}

/** Vite's `vite:preloadError` event: `payload` is the import's error. */
type PreloadErrorEvent = Event & { payload?: unknown };

/**
 * Reloads once when Vite reports a missing chunk or CSS file
 * (`vite:preloadError`, dispatched by the production build for every dynamic
 * import, also those outside the router). The event is not prevented: Vite
 * would then resolve the import with `undefined`, which fails later with a
 * misleading error. The import rejects into its error screen, which sees the
 * pending reload and stays quiet. Returns the cleanup.
 */
export function installStaleBundleRecovery(
  target: Pick<
    EventTarget,
    "addEventListener" | "removeEventListener"
  > = globalThis,
  reloadOnce: () => boolean = reloadForStaleBundle,
): () => void {
  const onPreloadError = (event: Event) => {
    // The event also carries errors thrown while a loaded module evaluates;
    // only a missing chunk is fixed by a reload.
    const { payload } = event as PreloadErrorEvent;
    if (isStaleBundleError(payload) && !isBrowserOffline()) {
      reloadOnce();
    }
  };
  target.addEventListener("vite:preloadError", onPreloadError);
  return () => target.removeEventListener("vite:preloadError", onPreloadError);
}
